/**
 * SAFE-8: Stress Test API client.
 * Handles SSE streaming for POST requests (can't use EventSource for POST)
 * and standard CRUD for test history and review.
 */
import { apiFetch, API_BASE } from "./api.js";
import { getAccessToken } from "../context/AuthContext.jsx";

/**
 * Run a stress test battery via streaming POST.
 * SSE events: "progress", "result", "completed"
 *
 * @param {string} personaId
 * @param {object} opts
 * @param {string[]} [opts.promptIds] - subset of prompt IDs to run
 * @param {function} [opts.onProgress] - called with progress data
 * @param {function} [opts.onResult] - called with each prompt result
 * @param {function} [opts.onCompleted] - called with final summary
 * @param {AbortSignal} [opts.signal] - abort signal to cancel
 * @returns {Promise<object>} final summary
 */
export async function runStressTest(personaId, opts = {}) {
  const { promptIds, onProgress, onResult, onCompleted, signal } = opts;

  const headers = {
    "Content-Type": "application/json",
    "X-API-Secret": import.meta.env.VITE_API_SECRET ?? "",
    Accept: "text/event-stream",
  };

  const jwt = getAccessToken();
  if (jwt) headers["Authorization"] = `Bearer ${jwt}`;

  const adminToken = sessionStorage.getItem("qm_admin_token");
  if (adminToken) headers["X-Admin-Token"] = adminToken;

  const res = await fetch(`${API_BASE}/api/admin/stress-test/${personaId}`, {
    method: "POST",
    headers,
    body: JSON.stringify({ promptIds: promptIds ?? undefined }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Stress test failed: ${res.status}`);
  }

  // Parse SSE stream manually
  if (!res.body) throw new Error("Response body is null — streaming not supported");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalResult = null;
  let lastTestId = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop(); // keep incomplete line in buffer

    let eventType = null;
    for (const line of lines) {
      if (line.startsWith("event: ")) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith("data: ")) {
        const data = line.slice(6);
        try {
          const parsed = JSON.parse(data);
          if (parsed.testId) lastTestId = parsed.testId;
          if (eventType === "progress" && onProgress) onProgress(parsed);
          else if (eventType === "result" && onResult) onResult(parsed);
          else if (eventType === "completed") {
            finalResult = parsed;
            if (onCompleted) onCompleted(parsed);
          }
        } catch { /* ignore parse errors */ }
        eventType = null;
      }
    }
  }

  // If stream ended without completed event (navigated away / network drop),
  // extract testId from progress events and fall back to polling
  if (!finalResult && !signal?.aborted && lastTestId) {
    return { testId: lastTestId, polling: true };
  }

  return finalResult;
}

/**
 * Poll a running stress test until it completes.
 * Used as fallback when SSE stream disconnects (page navigation, network drop).
 * Returns the completed test result.
 */
export async function pollForCompletion(personaId, testId, { interval = 5000, maxAttempts = 120, onUpdate } = {}) {
  for (let i = 0; i < maxAttempts; i++) {
    const data = await getStressTest(personaId, testId);
    if (onUpdate) onUpdate(data);
    // "RUNNING" means still in progress; anything else means done
    if (data.recommendation && data.recommendation !== "RUNNING") {
      return data;
    }
    await new Promise(r => setTimeout(r, interval));
  }
  throw new Error("Stress test timed out waiting for completion");
}

/** Get the prompt battery configuration. */
export async function getBattery() {
  return apiFetch("/api/admin/stress-test/battery");
}

/** List past stress test runs for a persona. */
export async function getStressTests(personaId, limit = 20, offset = 0) {
  return apiFetch(`/api/admin/stress-test/${personaId}?limit=${limit}&offset=${offset}`);
}

/** Get a single stress test result. */
export async function getStressTest(personaId, testId) {
  return apiFetch(`/api/admin/stress-test/${personaId}/${testId}`);
}

/** Submit a human review for a single prompt in a test run. */
export async function reviewPrompt(personaId, testId, promptId, humanVerdict, reviewNote) {
  return apiFetch(`/api/admin/stress-test/${personaId}/${testId}/review`, {
    method: "PATCH",
    body: JSON.stringify({ promptId, humanVerdict, reviewNote: reviewNote || undefined }),
  });
}
